// src/quality-management/components/EditTaskModal.tsx
// Reescrito sobre o Modal genérico do UI kit (nova identidade)

import React, { useState, useEffect, useContext } from 'react';
import { Task, UpdateTaskRequest, TaskStatus } from '../services/api/types';
import { useMutation } from '../hooks/useApi';
import { apiService } from '../services/api';
import { predecessorMinStart, formatDayBR } from '../utils/taskSchedule';
import { isManagementRole } from '../../shared/lib/roles';
import { AuthContext } from '../contexts/AuthAdapter';
import ConfirmationModal from './ConfirmationModal';
import { Modal } from '../../shared/components/ui/Modal';
import { Button } from '../../shared/components/ui/Button';
import { Input } from '../../shared/components/ui/Input';
import { Textarea } from '../../shared/components/ui/Textarea';
import { Select } from '../../shared/components/ui/Select';

interface EditTaskModalProps {
  task: Task;
  onClose: () => void;
  onTaskUpdated: () => void;
  onTaskDeleted?: () => void;
}

const FORM_ID = 'edit-task-form';

const statusOptions = [
  { value: 'pending', label: 'Pendente' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'pending_overtime_approval', label: 'Aguardando aprovação HE' },
];

const EditTaskModal: React.FC<EditTaskModalProps> = ({ task, onClose, onTaskUpdated, onTaskDeleted }) => {
  const { user } = useContext(AuthContext);
  const [taskName, setTaskName] = useState(task.name);
  const [description, setDescription] = useState(task.description || '');
  const [expectedHours, setExpectedHours] = useState<number>(task.expected_hours || 8);
  const [completionPercentage, setCompletionPercentage] = useState<number>(task.completion_percentage || 0);
  const [status, setStatus] = useState(task.status);
  const [predecessorId, setPredecessorId] = useState<string>(
    task.predecessor_id ? String(task.predecessor_id) : ''
  );
  const [taskDateStr, setTaskDateStr] = useState<string>(task.task_date ? task.task_date.slice(0, 10) : '');
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [error, setError] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    setTaskName(task.name);
    setDescription(task.description || '');
    setExpectedHours(task.expected_hours || 8);
    setCompletionPercentage(task.completion_percentage || 0);
    setStatus(task.status);
    setPredecessorId(task.predecessor_id ? String(task.predecessor_id) : '');
    setTaskDateStr(task.task_date ? task.task_date.slice(0, 10) : '');
  }, [task]);

  // Tarefas do projeto para o vínculo de predecessora
  useEffect(() => {
    let active = true;
    apiService.tasks
      .getTasks({ project_id: task.project_id })
      .then((ts) => {
        if (active) setProjectTasks(Array.isArray(ts) ? ts : []);
      })
      .catch((e) => console.error('Error fetching project tasks:', e));
    return () => {
      active = false;
    };
  }, [task.project_id]);

  // Candidatas a predecessora: exclui a própria tarefa e qualquer tarefa que
  // dependa dela (direta ou indiretamente), para não criar ciclo
  const predecessorOptions = React.useMemo(() => {
    const predMap = new Map<number, number | null | undefined>();
    projectTasks.forEach((t) => predMap.set(t.id, t.predecessor_id));
    const dependsOnCurrent = (id: number): boolean => {
      const seen = new Set<number>();
      let cur = predMap.get(id);
      while (cur != null && !seen.has(cur)) {
        if (cur === task.id) return true;
        seen.add(cur);
        cur = predMap.get(cur);
      }
      return false;
    };
    return projectTasks
      .filter((t) => t.id !== task.id && !dependsOnCurrent(t.id))
      .map((t) => ({ value: t.id, label: t.name || t.title || `Tarefa ${t.id}` }));
  }, [projectTasks, task.id]);

  const selectedPredecessor =
    projectTasks.find((t) => String(t.id) === predecessorId) ||
    (task.predecessor && String(task.predecessor.id) === predecessorId ? task.predecessor : null);
  const minStart = selectedPredecessor ? predecessorMinStart(selectedPredecessor) : null;

  // Ao vincular uma predecessora, empurra a data de início para o mínimo permitido
  const handlePredecessorChange = (value: string) => {
    setPredecessorId(value);
    const pred = projectTasks.find((t) => String(t.id) === value);
    const min = pred ? predecessorMinStart(pred) : null;
    if (min && (!taskDateStr || taskDateStr < min)) setTaskDateStr(min);
  };

  useEffect(() => {
    if (completionPercentage >= 100) {
      setStatus('completed');
    } else if (completionPercentage > 0) {
      setStatus('in_progress');
    }
  }, [completionPercentage]);

  const { mutate: updateTask, loading: isSubmitting } = useMutation(
    (data: UpdateTaskRequest) => apiService.tasks.updateTask(task.id, data),
    {
      onSuccess: () => {
        onTaskUpdated();
        onClose();
      },
      onError: (error) => {
        setError(error.message || 'Erro ao atualizar tarefa. Tente novamente.');
      },
    }
  );

  const { mutate: deleteTask, loading: isDeleting } = useMutation(
    (taskId: number) => apiService.tasks.deleteTask(taskId),
    {
      onSuccess: () => {
        if (onTaskDeleted) onTaskDeleted();
        onClose();
      },
      onError: (error) => {
        setError(error.message || 'Erro ao deletar tarefa. Tente novamente.');
        setShowDeleteConfirmation(false);
      },
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskName.trim()) return setError('Nome da tarefa é obrigatório');
    if (expectedHours <= 0) return setError('Horas esperadas devem ser maiores que 0');
    if (completionPercentage < 0 || completionPercentage > 100) return setError('Percentual de conclusão deve estar entre 0 e 100');
    if (minStart && taskDateStr && taskDateStr < minStart)
      return setError(`A data de início deve ser a partir de ${formatDayBR(minStart)}, um dia após a conclusão da predecessora`);
    if (
      selectedPredecessor &&
      ['in_progress', 'completed'].includes(status) &&
      selectedPredecessor.status !== 'completed'
    )
      return setError(
        `A predecessora "${selectedPredecessor.name || selectedPredecessor.title}" precisa estar concluída antes de iniciar esta tarefa`
      );
    setError('');
    await updateTask({
      name: taskName.trim(),
      description: description.trim() || undefined,
      expected_hours: expectedHours,
      completion_percentage: completionPercentage,
      status,
      task_date: taskDateStr || undefined,
      predecessor_id: predecessorId ? Number(predecessorId) : null,
    });
  };

  const handleClose = () => {
    if (!isSubmitting && !isDeleting) onClose();
  };

  // A role aqui vem do AuthAdapter ('Project Manager'), então a comparação
  // direta com 'manager' falhava e escondia o botão de admins e gerentes.
  const canDeleteTask = () => {
    if (!user) return false;
    if (isManagementRole(user.role)) return true;
    return task.created_by === user.id;
  };

  const formatTaskDate = () => {
    if (!taskDateStr) return '';
    const date = new Date(taskDateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title="Editar tarefa"
      size="lg"
      closeOnBackdrop={!isSubmitting && !isDeleting}
      data-testid="edit-task-modal"
      footer={
        <>
          {canDeleteTask() && (
            <Button
              type="button"
              variant="danger"
              onClick={() => setShowDeleteConfirmation(true)}
              loading={isDeleting}
              disabled={isSubmitting || isDeleting}
              className="mr-auto"
              data-testid="task-delete"
            >
              Excluir tarefa
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting || isDeleting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={isSubmitting}
            disabled={isSubmitting || isDeleting || !taskName.trim()}
            data-testid="task-save"
          >
            Atualizar tarefa
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-5">
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-sm text-danger">
            {error}
          </div>
        )}

        {taskDateStr && (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3.5 py-2.5 text-sm text-foreground-muted">
            <span className="font-medium text-foreground">Data da tarefa:</span>
            <span className="capitalize">{formatTaskDate()}</span>
          </div>
        )}

        {/* Vínculo de predecessora e data de início */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            id="edit-task-predecessor"
            name="edit-task-predecessor"
            label="Atividade predecessora"
            placeholder="Sem predecessora"
            value={predecessorId}
            onChange={(e) => handlePredecessorChange(e.target.value)}
            disabled={isSubmitting}
            data-testid="edit-task-predecessor-select"
            options={predecessorOptions}
          />
          <Input
            id="edit-task-date"
            name="edit-task-date"
            type="date"
            label="Data de início"
            value={taskDateStr}
            min={minStart ?? undefined}
            onChange={(e) => setTaskDateStr(e.target.value)}
            disabled={isSubmitting}
            data-testid="edit-task-date-input"
          />
        </div>

        {selectedPredecessor && minStart && (
          <div className="rounded-lg border border-brand/20 bg-brand/10 px-3.5 py-2.5 text-sm text-foreground">
            Esta tarefa depende de <strong>{selectedPredecessor.name || selectedPredecessor.title}</strong>
            {selectedPredecessor.status !== 'completed' && ' (ainda não concluída)'}.
            O início mais cedo permitido é <strong>{formatDayBR(minStart)}</strong>.
          </div>
        )}

        <Input
          id="task-name"
          name="task-name"
          label="Nome da tarefa *"
          placeholder="Digite o nome da tarefa"
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
          required
          disabled={isSubmitting}
          data-testid="task-name-input"
        />

        <Textarea
          id="task-description"
          name="task-description"
          label="Descrição"
          placeholder="Descreva os objetivos e requisitos da tarefa"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          disabled={isSubmitting}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            id="expected-hours"
            name="expected-hours"
            label="Horas esperadas *"
            type="number"
            min="0.5"
            step="0.5"
            value={expectedHours}
            onChange={(e) => setExpectedHours(parseFloat(e.target.value) || 0)}
            required
            disabled={isSubmitting}
          />
          <Input
            id="completion-percentage"
            name="completion-percentage"
            label="Conclusão %"
            type="number"
            min="0"
            max="100"
            step="5"
            value={completionPercentage}
            onChange={(e) => setCompletionPercentage(parseInt(e.target.value) || 0)}
            required
            disabled={isSubmitting}
          />
        </div>

        <Select
          id="task-status"
          name="task-status"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as TaskStatus)}
          disabled={isSubmitting}
          options={statusOptions}
          data-testid="task-status-select"
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">Progresso: {completionPercentage}%</span>
          <div className="h-2 overflow-hidden rounded-full bg-surface-3">
            <div
              className={completionPercentage === 100 ? 'h-full rounded-full bg-success' : 'h-full rounded-full bg-brand'}
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>
      </form>

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => deleteTask(task.id)}
        title="Excluir tarefa"
        message="Tem certeza que deseja excluir esta tarefa? Esta ação não pode ser desfeita e removerá todos os registros de tempo associados."
        confirmText="Sim, excluir"
        cancelText="Cancelar"
        type="danger"
        projectName={task.name}
      />
    </Modal>
  );
};

export default EditTaskModal;
